import config, { convergeLogger } from '@converge/config';
import { useCallback, useEffect, useRef } from 'react';

import { handleError } from '@converge/utils';
import { useAuthContext } from './require-auth';

import {
  hasTokenExpired,
  useRefreshAccessTokenMutation,
} from '@converge/graphql';
import moment from 'moment';
import {
  GetCurrentUserFromCookies,
  getExpiresEpoch,
  getSSOCookies,
  getUserEmailFromToken,
  hasSSOCookies,
  isLocalEnv,
} from './auth-provider';

const REFRESH_COOLDOWN_MS = 30000;

// let NEEDS_REFRESH_ON_LOAD = true;
const refreshTokenCall = async () => {
  try {
    let cookieData = getSSOCookies();
    const initalEpoch = getExpiresEpoch(cookieData);
    const response = await fetch('/auth/refresh', {
      method: 'GET',
      cache: 'no-store',
      credentials: 'include',
    });

    cookieData = getSSOCookies();
    const newEpoch = getExpiresEpoch(cookieData);
    const didNotRefresh = initalEpoch === newEpoch;
    if (didNotRefresh || response.status === 302) {
      const userData = getUserEmailFromToken(cookieData['idToken'] || '');
      const { username, firstName, lastName } = userData;
      const userDataMessage = `username=${username}, firstName=${firstName}, lastName=${lastName}`;
      const nowEpoch = Math.floor(Date.now() / 1000);
      const secondsRemaining = newEpoch - nowEpoch;

      const expiresInMessage =
        secondsRemaining > 0
          ? `expires in ${secondsRemaining}s (~${Math.round(
              secondsRemaining / 60
            )}m)`
          : `expired ${Math.abs(secondsRemaining)}s ago`;

      const errorMessage =
        response.status === 302
          ? 'refreshTokenCall returned 302'
          : `Token refresh did not actually refresh.
       prevEpoch=${initalEpoch}, newEpoch=${newEpoch}, ${expiresInMessage}.
       User data: ${userDataMessage}.`;

      handleError({
        source: 'receiveRaterMessageAndUpdateState',
        message: errorMessage,
      });
      if (secondsRemaining <= 0) {
        console.warn(
          'Token has expired and refresh failed. Forcing page reload to trigger fresh login.'
        );

        window.location.reload();
        return false;
      }
    } else if (!response.ok) {
      throw new Error('Token refresh failed');
    }
    return true;
  } catch (error) {
    console.error('Refresh failed, redirecting to logout', { error });
    return false;
  }
};

export const useAuthTokenRefresh = () => {
  const { user, setSignedInUser, resetUser, isUserIdle } = useAuthContext();
  const { refreshAccessToken } = useRefreshAccessTokenMutation();

  const lastRefreshAttempt = useRef(0);

  const refreshToken = useCallback(async () => {
    if (!user) {
      resetUser();
      convergeLogger.error('Unable to refresh token because there is no user.');
      return;
    }
    convergeLogger.log(`Refreshing token at ${new Date()}...`);

    const today = new Date().toISOString();
    const refreshTokenExpiryDate = JSON.parse(
      localStorage.getItem('refreshTokenExpiryDate') as string
    );
    const refreshTokenExpired =
      moment
        .duration(moment(today).diff(moment(refreshTokenExpiryDate)))
        .asSeconds() > 0;
    const ssoLogin = hasSSOCookies() && !isLocalEnv;
    try {
      const refreshRegularLogin =
        refreshTokenExpiryDate &&
        !refreshTokenExpired &&
        user?.refreshToken != null;
      if (refreshRegularLogin || ssoLogin) {
        if (!ssoLogin) {
          const { data } = await refreshAccessToken({
            variables: {
              refreshToken: user?.refreshToken || '',
            },
          });

          if (data?.refreshAccessToken) {
            convergeLogger.log(`Token refreshed at ${new Date()}.`);

            setSignedInUser(data.refreshAccessToken);
          }
        } else {
          await refreshTokenCall();
          const newUser = GetCurrentUserFromCookies();
          setSignedInUser(newUser);
        }
      } else {
        resetUser();
        window.location.reload();
      }
    } catch (error) {
      convergeLogger.error(error);
      if (hasTokenExpired(user.expiresEpoch)) {
        resetUser();
      }
      handleError({
        cause: error,
        message: `Failed to refresh token: ${error}`,
        source: 'useAuthTokenRefresh',
        context: {
          user: {
            ...(user ?? {}),
          },
        },
      });
    }
  }, [refreshAccessToken, resetUser, setSignedInUser, user]);

  const refreshTokenCheck = useCallback(() => {
    if (!user) {
      return;
    }

    // Do not silently renew tokens while the user is idle; otherwise the
    // inactivity timeout (see useIdleLogout) could never take effect.
    if (isUserIdle) {
      return;
    }

    const now = Date.now();
    if (now - lastRefreshAttempt.current <= REFRESH_COOLDOWN_MS) {
      return;
    }

    if (user) {
      if (hasTokenExpired(user.expiresEpoch)) {
        convergeLogger.debug('Token expired, refreshing...');
        lastRefreshAttempt.current = now;
        refreshToken();
      }
    }
  }, [user, isUserIdle, refreshToken]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshTokenCheck();
    }, config.refreshAccessTokenEveryMinutes * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [user, refreshTokenCheck]);

  useEffect(() => {
    refreshTokenCheck();
  }, [refreshTokenCheck]);
  return;
};
